// Vite emits `vite:preloadError` when a dynamically-imported chunk fails to
// load. The most common production cause is a stale `index.html` referencing
// chunk hashes that were replaced by a deploy. Reloading once fixes it.
//
// We avoid reloading on transient network failures (offline, server hiccup)
// and guard against reload loops if the chunk is genuinely missing.
//
// We deliberately do NOT call `event.preventDefault()`: we want Vite to throw
// the original error so it propagates to the React `ErrorBoundary`. If we go
// on to reload, the reload replaces the boundary UI with a fresh page; if we
// don't reload (offline / server down / loop guard), the user sees the real
// error page rather than an infinite Suspense spinner.

const RELOAD_KEY = "igfd:preloadError:reloaded";

// Delay clearing the loop-guard after `load` so that any preload errors fired
// during the post-reload mount still hit the guard. Without this delay, the
// lazy import that just failed before the reload would reload us again.
const RELOAD_GUARD_RETENTION_MS = 10_000;

export type PreloadErrorOutcome =
  | "reloaded"
  | "skipped-offline"
  | "skipped-server-unreachable"
  | "skipped-loop-guard";

export interface PreloadErrorHandlerDeps {
  isOnline: () => boolean;
  pingDocument: () => Promise<boolean>;
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  reload: () => void;
}

export async function handlePreloadError(
  deps: PreloadErrorHandlerDeps
): Promise<PreloadErrorOutcome> {
  if (!deps.isOnline()) return "skipped-offline";

  const reachable = await deps.pingDocument();
  if (!reachable) return "skipped-server-unreachable";

  if (deps.storage.getItem(RELOAD_KEY)) return "skipped-loop-guard";

  deps.storage.setItem(RELOAD_KEY, "1");
  deps.reload();
  return "reloaded";
}

export function installPreloadErrorHandler(): void {
  window.addEventListener("vite:preloadError", () => {
    void handlePreloadError({
      isOnline: () => navigator.onLine,
      pingDocument: async () => {
        try {
          const res = await fetch("/", { method: "HEAD", cache: "no-store" });
          return res.ok;
        } catch {
          return false;
        }
      },
      storage: window.sessionStorage,
      reload: () => window.location.reload(),
    });
  });

  // Once a fresh page has fully loaded AND survived its first lazy imports,
  // clear the loop-guard so a future deploy in the same session can trigger
  // another reload. We delay the clear so that if the post-reload render
  // immediately re-encounters a preload failure, the loop-guard is still set
  // and the user lands on the ErrorBoundary instead of a reload loop.
  window.addEventListener("load", () => {
    window.setTimeout(() => {
      window.sessionStorage.removeItem(RELOAD_KEY);
    }, RELOAD_GUARD_RETENTION_MS);
  });
}
